/**
 * Výber kovania v okne (KOLO 202) — Zmeniť kovanie, Vymeniť kovanie
 * a produkty Korpus Buildera. Rám je ten istý ako pri hranách a materiáli
 * (`VyberDialog`, KOLO 201): klik označí, dvojklik / Enter / tlačidlo
 * potvrdí. Do KOLA 202 každé z týchto okien menilo kovanie **prvým
 * klikom** a hromadná výmena tak išla do všetkých dielcov bez otázky.
 *
 * Okno začína v kategórii kovania, ktoré sa mení (rodič skupiny — záves
 * s tlmením otvorí Závesy), s ★ Obľúbenými; Korpus Builder mu kategóriu
 * dáva sám (Nohy, Výsuvy, Závesy).
 */
import { useEffect, useState, type ReactNode } from "react";
import { useMaterialDetail } from "@/api/hooks/useCennik";
import { KovanieCategoryPicker } from "@/components/dielec/KovanieCategoryPicker";
import { VyberDialog } from "@/components/vyber/VyberDialog";
import type { Material } from "@/api/schemas";

export interface VyberKovaniaDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (material: Material) => void;
  /** Kovanie, ktoré je teraz vybrané — v okne označené. */
  currentId?: string | null;
  nadpis: ReactNode;
  popis?: ReactNode;
  potvrdText?: ReactNode;
  /** Kategória, s ktorou okno začne (Korpus Builder). */
  pociatocnaKategoria?: string;
  /**
   * Kovanie, podľa ktorého okno začne v jeho kategórii — skupina zo
   * servera (`MaterialResponse.skupina`), inak kategória sama.
   */
  kategoriaPodla?: string | null;
  vynechat?: readonly string[];
  busy?: boolean;
}

export function VyberKovaniaDialog({
  open,
  onClose,
  onSelect,
  currentId = null,
  nadpis,
  popis = "★ Obľúbené zo stránky Kovania. Pri písaní sa hľadá v celom kovaní vybranej kategórie.",
  potvrdText = "Vybrať",
  pociatocnaKategoria,
  kategoriaPodla,
  vynechat,
  busy = false,
}: VyberKovaniaDialogProps): JSX.Element {
  const [picked, setPicked] = useState<string | null>(currentId);
  // Položka zo zoznamu príde celá; detail len pre `currentId`, na ktorý
  // sa v okne neklikol (ten istý dopyt ako pás Vybrané v pätičke)
  const [zoZoznamu, setZoZoznamu] = useState<Material | null>(null);
  useEffect(() => {
    if (open) {
      setPicked(currentId);
      setZoZoznamu(null);
    }
  }, [open, currentId]);
  const vybranaQuery = useMaterialDetail(picked ?? undefined);
  const podlaQuery = useMaterialDetail(
    !pociatocnaKategoria && kategoriaPodla ? kategoriaPodla : undefined,
  );
  const podla = podlaQuery.data?.data;
  const kategoria =
    pociatocnaKategoria ??
    (podla && podla.id === kategoriaPodla ? (podla.skupina ?? podla.kategoria ?? undefined) : undefined);
  // Kým nie je jasné, kde okno začne, zoznam sa nepýta — inak by prišiel
  // dvakrát a celé kovanie by bliklo pred kategóriou
  const pripraveny = Boolean(pociatocnaKategoria) || !kategoriaPodla || !podlaQuery.isLoading;

  function potvrd(m?: Material): void {
    if (busy) return;
    const detail = vybranaQuery.data?.data;
    const material =
      m ??
      (zoZoznamu?.id === picked ? zoZoznamu : undefined) ??
      (detail && detail.id === picked ? detail : undefined);
    if (material) onSelect(material);
  }

  return (
    <VyberDialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      nadpis={nadpis}
      popis={popis}
      vybraneId={picked}
      potvrdText={potvrdText}
      potvrdZakazane={busy}
      onPotvrd={() => potvrd()}
    >
      <KovanieCategoryPicker
        vOkne
        value={picked}
        onChange={(id, m) => {
          setPicked(id);
          setZoZoznamu(m ?? null);
        }}
        onPotvrd={(m) => potvrd(m as Material)}
        pociatocnaKategoria={kategoria}
        vynechat={vynechat}
        pripraveny={pripraveny}
      />
    </VyberDialog>
  );
}
